<script lang="ts">
  import YoutubeEmbed from '$lib/videos/YoutubeEmbed.svelte';
  import NavArrow from '$lib/common/gallery/NavArrow.svelte';
  import HandleSwipe from '$lib/common/HandleSwipe.svelte';
  import type { VideoItem } from '$lib/types';

  const videos: VideoItem[] = [
    { videoId: 'UJdhmVDIWSw', title: 'Illage Vidiot' },
    { videoId: 'ENi5SFBelGM', title: 'The Milestone' },
  ];

  let currentIndex = $state(0);

  // Calculate the horizontal shift (100% per video)
  const offset = $derived(currentIndex * 100);

  function nextVideo() {
    currentIndex = (currentIndex + 1) % videos.length;
  }

  function prevVideo() {
    currentIndex = (currentIndex - 1 + videos.length) % videos.length;
  }

  function goToVideo(index: number) {
    currentIndex = index;
  }
</script>

<HandleSwipe
  threshold={75}
  onSwipeLeft={nextVideo}
  onSwipeRight={prevVideo}
  class="video-gallery-viewport"
>
  {#if videos.length > 1}
    <div class="nav-controls">
      <NavArrow
        direction="PREV"
        onclick={prevVideo}
        ariaLabel="Previous video"
        class="nav-arrow prev"
      />
      <NavArrow
        direction="NEXT"
        onclick={nextVideo}
        ariaLabel="Next video"
        class="nav-arrow next"
      />
    </div>
  {/if}

  <div class="video-track" style:transform="translateX(-{offset}%)">
    {#each videos as video, i (i + video.title)}
      <div class="video-slide">
        <div class="responsive-container">
          <div class="aspect-ratio-box">
            <div class="content">
              <YoutubeEmbed {...video} current={i === currentIndex} />
            </div>
          </div>
        </div>
      </div>
    {/each}
  </div>

  {#if videos.length > 1}
    <div class="pagination-indicator">
      {#each videos as video, i (i + ' ' + video.videoId)}
        <button
          class="dot"
          class:active={i === currentIndex}
          onclick={() => goToVideo(i)}
          aria-label="Go to video {i + 1}"
        ></button>
      {/each}
    </div>
  {/if}
</HandleSwipe>

<style>
  :global(.video-gallery-viewport) {
    width: 100%;
    overflow: hidden;
    position: relative;
    background: #000;
    padding-bottom: 2.5rem; /* Increased space for pagination dots */
  }

  .video-track {
    display: flex;
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .video-slide {
    flex: 0 0 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    /* Edge-to-edge on mobile */
    padding: 0;
    box-sizing: border-box;
  }

  /* Restore original wide feel for Tablet/Desktop */
  @media (min-width: 768px) {
    .video-slide {
      padding: 0 20px; /* Matches your original padding */
    }
  }

  @media (min-width: 1024px) {
    .video-slide {
      padding: 0 40px; /* Slight extra padding for very large screens */
    }
  }

  .responsive-container {
    width: 100%; /* Remove the 65%/85% constraints to restore original width */
    max-width: 1400px; /* Optional: prevents video from being comically large on ultra-wide monitors */
    transition: width 0.3s ease;
  }

  .aspect-ratio-box {
    position: relative;
    width: 100%;
    padding-top: 56.25%; /* 16:9 Aspect Ratio [cite: 70, 102] */
  }

  .content {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }

  /* Pagination Styling */
  .pagination-indicator {
    display: flex;
    justify-content: center;
    gap: 8px; /* Mobile Gap */
    margin-top: 1rem;
  }

  .dot {
    width: 8px; /* Mobile Size */
    height: 8px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    border: none;
    padding: 0;
    cursor: pointer;
    transition:
      background-color 0.3s,
      transform 0.3s;
  }

  /* Tablet: 2x Size and Spread  */
  @media (min-width: 768px) {
    .pagination-indicator {
      gap: 16px; /* 2x Spread */
    }
    .dot {
      width: 16px; /* 2x Size */
      height: 16px;
    }
  }

  /* Desktop: 1.5x Size and Spread  */
  @media (min-width: 1024px) {
    .pagination-indicator {
      gap: 12px; /* 1.5x Spread */
    }
    .dot {
      width: 12px; /* 1.5x Size */
      height: 12px;
    }
  }

  .dot.active {
    background: var(--light-purple, #fff);
    transform: scale(1.2);
  }

  /* Keep your existing rule to hide dots on ultra-wide desktops if preferred [cite: 45, 77] */
  @media (min-width: 1280px) {
    .pagination-indicator {
      display: none;
    }
  }

  /* Arrow Visibility: Desktop Only logic */
  @media (hover: hover) {
    :global(.nav-arrow) {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 10;
      opacity: 0;
      transition: opacity 0.3s;
    }

    :global(.video-gallery-viewport:hover) :global(.nav-arrow) {
      opacity: 1;
    }
  }

  /* Hide arrows on touch devices (Mobile/Tablet) */
  @media (hover: none) {
    :global(.nav-arrow) {
      display: none;
    }
  }

  :global(.prev) {
    left: 1rem;
  }
  :global(.next) {
    right: 1rem;
  }
</style>
